import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { AlertCircleIcon, SpinnerIcon } from "../../../Icons";
import {
  MODAL_BACKDROP,
  MODAL_PANEL,
  BTN_SECONDARY,
} from "../../../ui/pageChrome";
import { PATIENT_THEME } from "../patientTheme";

/**
 * Confirm archive / unarchive of a patient (Admin only).
 */
const PatientArchiveConfirmModal = ({
  open,
  patient,
  mode = "archive",
  loading = false,
  onClose,
  onConfirm,
}) => {
  const isUnarchive = mode === "unarchive";

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !loading) onClose?.();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, loading, onClose]);

  if (!open || !patient) return null;

  const name = [patient.lastName, patient.firstName]
    .map((p) => String(p || "").trim())
    .filter(Boolean)
    .join(", ");
  const label = name || patient.mrn || `Patient #${patient.id}`;

  return createPortal(
    <div
      className={MODAL_BACKDROP}
      role="dialog"
      aria-modal="true"
      aria-labelledby="patient-archive-confirm-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !loading) onClose?.();
      }}
    >
      <div
        className={`${MODAL_PANEL} max-w-md overflow-hidden`}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div
          className={`flex items-start gap-3 px-4 py-3.5 border-b ${PATIENT_THEME.modalHead}`}
        >
          <span
            className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${
              isUnarchive ? "bg-indigo-100" : "bg-amber-100"
            }`}
          >
            <AlertCircleIcon
              className={`h-5 w-5 ${
                isUnarchive ? "text-indigo-600" : "text-amber-700"
              }`}
              aria-hidden
            />
          </span>
          <div className="min-w-0">
            <h3
              id="patient-archive-confirm-title"
              className={`text-sm font-semibold ${PATIENT_THEME.modalTitle}`}
            >
              {isUnarchive ? "Unarchive patient?" : "Archive patient?"}
            </h3>
            <p className="mt-1 text-[13px] text-slate-600 leading-relaxed">
              {isUnarchive ? (
                <>
                  Restore{" "}
                  <span className="font-semibold text-slate-800">{label}</span>
                  {patient.mrn ? (
                    <>
                      {" "}
                      (<span className="tabular-nums">{patient.mrn}</span>)
                    </>
                  ) : null}{" "}
                  to the active patient list. Assigned staff will be able to
                  access this patient again.
                </>
              ) : (
                <>
                  Archive{" "}
                  <span className="font-semibold text-slate-800">{label}</span>
                  {patient.mrn ? (
                    <>
                      {" "}
                      (<span className="tabular-nums">{patient.mrn}</span>)
                    </>
                  ) : null}
                  . Only Admins can restore archived patients from the archived list.
                </>
              )}
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-2 px-4 py-3 bg-slate-50/80">
          <button
            type="button"
            disabled={loading}
            onClick={onClose}
            className={BTN_SECONDARY}
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={loading}
            onClick={onConfirm}
            className={`inline-flex items-center justify-center gap-1.5 h-9 px-3 text-sm font-semibold rounded-md text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              isUnarchive
                ? "bg-indigo-600 hover:bg-indigo-700"
                : "bg-amber-600 hover:bg-amber-700"
            }`}
          >
            {loading ? <SpinnerIcon className="animate-spin" size={14} /> : null}
            {isUnarchive ? "Unarchive" : "Archive"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default PatientArchiveConfirmModal;
